import React, { memo, useEffect, useRef, useState } from 'react'
import Header from './Header';
import { useDispatch, useSelector } from 'react-redux';
import { discardSentenceForUserDict, fetchPartsOfSpeech, fetchWordDetails, getRejectedSentencesInALotDict, getSentencesInALotDict, rejectSentenceForUser, rejectSentenceForUserDict, resolveSentenceForUser, resolveSentenceForUserDict, reviewSentenceForStudentDict, updateWordDetails, validateSentenceDict } from '@/store/thunks/studentTTT';
import GenericTableStudent from '../GenericTableStudent';
import { BASE_URL } from "@/utils/constants";
import { toast } from 'react-toastify';
import apiClient from '@/services/api/apiClientStudent';


const normalizeWordDetails = (data) => {
  return {
    translations: (data.translations || []).map(obj => {
      const [backendId, value] = Object.entries(obj)[0] || [];
      return {
        uiId: crypto.randomUUID(),
        backendId: Number(backendId), // ✅ KEEP BACKEND ID
        value: value ?? ""
      };
    }),

    meanings: (data.meanings || []).map(obj => {
      const [backendId, inner] = Object.entries(obj)[0] || [];
      const [posKey, meaningValue] = Object.entries(inner || {})[0] || [];

      return {
        uiId: crypto.randomUUID(),
        backendId: Number(backendId), // ✅ KEEP BACKEND ID
        partOfSpeech: Number(posKey),
        value: meaningValue ?? ""
      };
    }),

    examples: data.example
      ? (() => {
        const [backendId, value] = Object.entries(data.example)[0] || [];
        return [{
          uiId: crypto.randomUUID(),
          backendId: Number(backendId), // ✅ KEEP BACKEND ID
          value: value ?? ""
        }];
      })()
      : []
  };
};



const normalizeWordDetailsReviewer = (data) => {
  return {
    translations: data.studentTranslations.map(obj => {
      const [backendId, value] = Object.entries(obj)[0];
      return {
        uiId: crypto.randomUUID(),
        backendId: Number(backendId),
        studentValue: value,
        reviewerValue: value // initially same
      };
    }),

    meanings: data.studentMeanings.map(obj => {
      const [backendId, inner] = Object.entries(obj)[0];
      const [posId, value] = Object.entries(inner)[0];

      return {
        uiId: crypto.randomUUID(),
        backendId: Number(backendId),
        partOfSpeech: Number(posId),
        studentValue: value,
        reviewerValue: value // initially same
      };
    }),

    examples: data.studentExample
      ? (() => {
        const [backendId, value] = Object.entries(data.studentExample)[0];
        return [{
          uiId: crypto.randomUUID(),
          backendId: Number(backendId),
          studentValue: value,
          reviewerValue: value
        }];
      })()
      : []
  };
};


const normalize = (v = "") => v.trim().toLowerCase();

const hasDuplicates = (items) => {
  const seen = new Set();
  for (let item of items) {
    const val = normalize(item.value);
    if (!val) continue;
    if (seen.has(val)) return true;
    seen.add(val);
  }
  return false;
};


const CommentsModal = memo(function CommentsModal({
  isOpen,
  comments = [],
  onClose,
  loading = false
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h3 className="text-2xl font-bold text-gray-800">Review Comments</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-3xl font-light hover:scale-110 transition"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg">No review comments yet.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {comments.map((c, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {c.studentUsername?.[0]?.toUpperCase() || "S"}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {c.studentUsername || "Unknown Student"}
                        </p>
                        <p className="text-sm text-amber-600 font-medium">
                          Target: {c.targetName || "Unknown"}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                      {new Date(c.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                  </div>

                  <div className="mt-3 pl-13">
                    <p className="text-gray-800 whitespace-pre-wrap leading-relaxed bg-white p-4 rounded-lg border-l-4 border-amber-400">
                      {c.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ReviewModal = memo(function ReviewModal({
  isOpen,
  row,
  comment,
  onCommentChange,
  onClose,
  onSubmit,
  translatedText,
  loading
}) {
  if (!isOpen || !row) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Add Review</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source (English)
              </label>
              <p className="p-3 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap">
                {row.original.word}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subdomain (English)
              </label>
              <p className="p-3 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap">
                {row.original.subdomainName}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Review / Comment <span className="text-red-500"> (Comment must contain at least 8 words.) *</span>
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none"
                rows="5"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="Write your feedback, suggestions, or corrections here..."
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={loading}
              className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition disabled:opacity-70"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const EditWordEditor = React.memo(function EditWordEditor({
  row,
  loadingEdit,
  sectionValues,
  setSectionValues,
  addField,
  updateField,
  removeField,
  onSave,
  onCancel,
  partsOfSpeech,
  role,
}) {


  const hasMeaningWithoutPOS = () => {
    return sectionValues.meanings.some(
      (m) => m.value?.trim() && !m.partOfSpeech
    );
  };

  const handleSave = () => {
    if (sectionValues.translations.length < 1) {
      toast.error("At least one translation is required.");
      return;
    }

    if (sectionValues.meanings.length < 1) {
      toast.error("At least one meaning is required.");
      return;
    }

    if (sectionValues.examples.length < 1) {
      toast.error("At least one example is required.");
      return;
    }

    if (hasDuplicates(sectionValues.translations)) {
      toast.error("Duplicate translations are not allowed.");
      return;
    }

    if (sectionValues.examples.length > 1) {
      toast.error("Only one example is allowed.");
      return;
    }


    if (hasDuplicates(sectionValues.meanings)) {
      toast.error("Duplicate meanings are not allowed.");
      return;
    }

    if (meaningsClashWithExamples()) {
      toast.error("Meanings and examples cannot have the same text.");
      return;
    }

    if (hasMeaningWithoutPOS()) {
      toast.error("Please select Part of Speech for all meanings.");
      return;
    }

    // ✅ All validations passed
    onSave();
  };

  const selectedPOS = sectionValues.meanings
    .map((m) => m.partOfSpeech)
    .filter(Boolean);

  const meaningsClashWithExamples = () => {
    const exampleSet = new Set(
      sectionValues.examples
        .map((e) => normalize(e.value))
        .filter(Boolean)
    );

    return sectionValues.meanings.some(
      (m) => exampleSet.has(normalize(m.value)) && m.value
    );
  };


  return (
    <div className="flex flex-col gap-4 w-full">
      {loadingEdit && (
        <span className="text-xs text-gray-500">Loading...</span>
      )}

      {/* TRANSLATIONS */}
      <div>

        <button
          onClick={() => {
            row.studentStatus !== 'VALIDATED' && addField("translations")
          }}
          className="flex items-center gap-2 text-sm font-semibold text-blue-700"
        >
          <span className="text-lg">➕</span>
          Add Translation(s)
        </button>

        {/* TEXTAREAS - CORRECTLY OUTSIDE THE BUTTON */}
        {sectionValues.translations.map((item, index) => (
          <div key={item.uiId} className="w-full flex items-start gap-2 mt-2">
            <textarea
              value={role === "REVIEWER" ? item.reviewerValue : item.value}
              onChange={(e) =>
                role === "REVIEWER"
                  ? updateField("translations", item.uiId, { reviewerValue: e.target.value })
                  : updateField("translations", item.uiId, e.target.value)
              }
              rows={2}
              placeholder="Enter translation"
              className="w-3/4 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-300 focus:outline-none"
            />

            {row.studentStatus !== 'VALIDATED' && sectionValues.translations.length > 1 &&
              index === sectionValues.translations.length - 1 && <button
                onClick={() => removeField("translations", item.uiId)}
                className="text-red-600 text-lg font-bold"
              >
                ➖
              </button>}
          </div>
        ))}
      </div>

      {/* MEANINGS */}
      <div>
        <button
          onClick={() => {
            row.studentStatus !== 'VALIDATED' && addField("meanings")
          }}
          className="flex items-center gap-2 text-sm font-semibold text-blue-700"
        >
          <span className="text-lg">➕</span>
          Add Meaning(s)
        </button>

        {sectionValues.meanings.map((item, index) => (
          <div
            key={item.uiId}
            className="w-full flex items-start gap-2 mt-2"
          >
            {/* Input group */}
            <div className="flex w-3/4 gap-2 mr-4">
              {/* Meaning */}
              <div className="w-3/4">
                <textarea
                  value={role === "REVIEWER" ? item.reviewerValue : item.value}
                  onChange={(e) =>
                    role === "REVIEWER"
                      ? updateField("meanings", item.uiId, { reviewerValue: e.target.value })
                      : updateField("meanings", item.uiId, e.target.value)
                  }
                  rows={2}
                  placeholder="Enter meaning"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md 
                     focus:ring-2 focus:ring-green-300 focus:outline-none"
                />
              </div>

              {/* Part of Speech */}
              <div className="w-1/4">
                <select
                  value={item.partOfSpeech ?? ""}
                  onChange={(e) =>
                    updateField("meanings", item.uiId, {
                      partOfSpeech: Number(e.target.value) // ensure ID is stored
                    })
                  }
                  className="w-full h-[45px] px-3 border border-gray-300 
     rounded-md bg-white focus:ring-2 focus:ring-green-300"
                >
                  <option value="">Select Part of Speech</option>

                  {partsOfSpeech
                    .filter(
                      (opt) =>
                        opt.posId === item.partOfSpeech ||
                        !selectedPOS.includes(opt.posId)
                    )
                    .map((opt) => (
                      <option key={opt.posId} value={opt.posId}>
                        {opt.partsOfSpeech}
                      </option>
                    ))}
                </select>

              </div>
            </div>

            {/* Remove */}
            {row.studentStatus !== 'VALIDATED' && sectionValues.meanings.length > 1 &&
              index === sectionValues.meanings.length - 1 && <button
                onClick={() => removeField("meanings", item.uiId)}
                className="text-red-600 text-lg font-bold mt-1"
                title="Remove meaning"
              >
                ➖
              </button>}
          </div>
        ))}

      </div>

      {/* EXAMPLES */}
      <div>
        <button
          onClick={() => {
            row.studentStatus !== 'VALIDATED' && addField("examples")
          }}
          disabled={sectionValues.examples.length >= 1}
          className={`flex items-center gap-2 text-sm font-semibold ${sectionValues.examples.length >= 1
            ? "text-gray-400 cursor-not-allowed"
            : "text-blue-700"
            }`}
        >
          Add Example
        </button>


        {sectionValues.examples.map((item) => (
          <div key={item.uiId} className="w-full flex items-start gap-2 mt-2">
            <textarea
              value={role === "REVIEWER" ? item.reviewerValue : item.value}
              onChange={(e) =>
                role === "REVIEWER"
                  ? updateField("examples", item.uiId, { reviewerValue: e.target.value })
                  : updateField("examples", item.uiId, e.target.value)
              }
              rows={2}
              placeholder="Enter example"
              className="w-3/4 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-300 focus:outline-none"
            />

          </div>
        ))}
      </div>


      <div className="flex gap-3 mt-4">
        {((row.studentStatus !== 'REVIEWED')) && <button
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium transition"
        >
          Validate
        </button>}
        <button
          onClick={onCancel}
          className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded font-medium transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
});

const Dictionary = () => {
  const dispatch = useDispatch();
  const { selectedLotId, selectedTargetId, selectedTargetName, showMarkedSentencesDict } = useSelector((state) => state.studentDashboard);
  const { userId, role } = useSelector((state) => state.studentAuth);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [doubleClickedRow, setDoubleClickedRow] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [pendingAction, setPendingAction] = useState(null);
  const [domain, setDomain] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editSections, setEditSections] = useState({
    translations: false,
    meanings: false,
    examples: false,
  });
  const [editModal, setEditModal] = useState({
    isOpen: false,
    row: null
  });

  const activeValidationToast = useRef(null);

  const [partsOfSpeech, setPartsOfSpeech] = useState([])

  const [sectionValues, setSectionValues] = useState({
    translations: [],
    meanings: [],
    examples: [],
  });
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loading, setLoading] = useState(true);

  const [files, setFiles] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    row: null,        // the row being reviewed
    comment: ""       // user's review comment
  });

  const [commentsModal, setCommentsModal] = useState({
    isOpen: false,
    mappingId: null,
    comments: [],
    loading: false
  });

  const [discardModal, setDiscardModal] = useState({
    isOpen: false,
    row: null,
    loading: false
  });


  const handleCommentChange = (newComment) => {
    setReviewModal(prev => ({ ...prev, comment: newComment }));
  };

  const closeReviewModal = () => {
    setReviewModal({ isOpen: false, row: null, comment: "" });
  };

  const handleReviewSubmit = async () => {
    if (!reviewModal.comment.trim()) {
      toast.error("Please write a review comment.");
      return;
    }

    setLoading(true);
    try {
      const absoluteIndex = pageIndex * pageSize + reviewModal.row.index;
      const translatedText = tableDataRef.current[absoluteIndex]?.targetText || "";

      const payload = {
        mappingId: reviewModal.row.original.mappingId,
        targetId: reviewModal.row.original.targetId,
        comment: reviewModal.comment.trim(),
      };

      const res = await dispatch(reviewSentenceForStudentDict(payload));

      if (res.success) {
        toast.success("Review submitted successfully!");
        await getsentencesForUser();
        setReviewModal({ isOpen: false, row: null, comment: "" });
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reviewSentence = (row) => {
    setReviewModal({
      isOpen: true,
      row,
      comment: ""
    });
  };


  // Add this line near your other useState
  const tableDataRef = React.useRef(tableData);

  // Add this effect — THIS IS THE KEY!
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);


  // ← ADD THIS HOOK (anywhere inside TTTCard)
  useEffect(() => {
    const isAnyModalOpen = reviewModal.isOpen || commentsModal.isOpen || !!pendingAction || editModal.isOpen;

    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [reviewModal.isOpen, commentsModal.isOpen, pendingAction, editModal.isOpen]);




  const paginatedData = React.useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return files.slice(start, end);
  }, [files, pageIndex, pageSize]);



  const getsentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getSentencesInALotDict(selectedLotId, selectedTargetId));
      if (res.data && Array.isArray(res.data)) {
        const freshData = res.data;
        setFiles(freshData);
        setDomain(freshData[0]?.subdomainName || null);
        setTableData(freshData.map(item => ({
          ...item,
          targetText: item.translatedText || ""
        })));
        setDataVersion(v => v + 1); // ← Force update
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };

  const getRejectedSentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getRejectedSentencesInALotDict(selectedLotId, selectedTargetId));
      if (res.data && Array.isArray(res.data)) {
        const freshData = res.data;
        setFiles(freshData);
        setTableData(freshData.map(item => ({
          ...item,
          targetText: item.translatedText || ""
        })));
        setDataVersion(v => v + 1); // ← Force update
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };


  const getPartsSpeech = async () => {
    setLoading(true);
    try {
      const res = await dispatch(fetchPartsOfSpeech());
      if (res.data && Array.isArray(res.data)) {
        setPartsOfSpeech(res.data);
      }
    } catch (err) {
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data from API
  useEffect(() => {
    if (activeTab === "active") {
      getsentencesForUser();
    } else if (activeTab === "rejected") {
      getRejectedSentencesForUser();
    }
  }, [dispatch, selectedLotId, selectedTargetId, activeTab]);


  // Fetch data from API
  useEffect(() => {
    getPartsSpeech();
  }, [dispatch]);

  const showReviewComments = async (row) => {
    console.log("Showing review comments for row:", row);
    const mappingId = row.original.mappingId;

    setCommentsModal({
      isOpen: true,
      mappingId,
      comments: [],
      loading: true
    });

    try {
      const url = role === 'STUDENT' ? `api/student/dt/comments?mappingId=${mappingId}&studentId=${row.original.studentId}` : `api/reviewer/dt/comments?mappingId=${mappingId}&studentId=${row.original.studentId}`;
      // Replace this with your real API endpoint
      const response = await apiClient(BASE_URL + url);

      setCommentsModal(prev => ({
        ...prev,
        comments: response.data || [],
        loading: false
      }));
    } catch (err) {
      console.log(err)
      toast.error("Failed to load comments.");
      setCommentsModal(prev => ({ ...prev, loading: false }));
    }
  };


  //------------------ editing --------------------

  const startEdit = async (row) => {
    setLoadingEdit(true);
    const mappingId = row.original.mappingId;
    setEditingRowId(mappingId);
    setEditModal({ isOpen: true, row });

    // reset first
    setEditSections({
      translations: false,
      meanings: false,
      examples: false,
    });


    setSectionValues({
      translations: [
        { uiId: crypto.randomUUID(), backendId: 0, value: "" }
      ],
      meanings: [
        { uiId: crypto.randomUUID(), backendId: 0, value: "", partOfSpeech: "" }
      ],
      examples: [
        { uiId: crypto.randomUUID(), backendId: 0, value: "" }
      ],
    });


    try {
      const res = await dispatch(
        fetchWordDetails(row.original.mappingId, row.original.targetId)
      );

      if (res?.success && res?.data) {
        const normalized = role === 'REVIEWER' ? normalizeWordDetailsReviewer(res.data) : normalizeWordDetails(res.data);

        setSectionValues({
          translations:
            normalized.translations.length > 0
              ? normalized.translations
              : [{ uiId: crypto.randomUUID(), backendId: 0, value: "" }],

          meanings:
            normalized.meanings.length > 0
              ? normalized.meanings
              : [{ uiId: crypto.randomUUID(), backendId: 0, value: "", partOfSpeech: "" }],

          examples:
            normalized.examples.length > 0
              ? normalized.examples
              : [{ uiId: crypto.randomUUID(), backendId: 0, value: "" }],
        });


        setEditSections({
          translations: normalized.translations.length > 0,
          meanings: normalized.meanings.length > 0,
          examples: normalized.examples.length > 0,
        });
      }

    } catch (err) {
      console.error(err);
      toast.error("Failed to load word details");
    } finally {
      setLoadingEdit(false);
    }
  };

  const saveEdit = async (row) => {
    setLoading(true);
    try {

      console.log(row);
      const buildUpdatePayload = () => {
        return {
          mappingId: row.original.mappingId,
          targetId: row.original.targetId,

          translations: sectionValues.translations
            .filter(t => t.value?.trim())
            .map(t => ({
              [t.backendId || 0]: t.value.trim()
            })),

          meanings: sectionValues.meanings
            .filter(m => m.value?.trim() && m.partOfSpeech)
            .map(m => ({
              [m.backendId || 0]: {
                [m.partOfSpeech]: m.value.trim()
              }
            })),

          example:
            sectionValues.examples.length > 0 &&
              sectionValues.examples[0].value?.trim()
              ? {
                [sectionValues.examples[0].backendId || 0]:
                  sectionValues.examples[0].value.trim()
              }
              : {}

        };
      };
      const buildReviewerPayload = (row) => ({
        mappingId: row.original.mappingId,
        targetId: row.original.targetId,
        studentId: row.original.studentId,
        targetName: selectedTargetName,

        translations: sectionValues.translations.map(t => ({
          student: t.studentValue,
          reviewer: t.reviewerValue
        })),

        meanings: sectionValues.meanings.map(m => ({
          [m.partOfSpeech]: {
            student: m.studentValue,
            reviewer: m.reviewerValue
          }
        })),

        example: sectionValues.examples.length
          ? {
            student: sectionValues.examples[0].studentValue,
            reviewer: sectionValues.examples[0].reviewerValue
          }
          : {}
      });


      const payload =
        role === "REVIEWER"
          ? buildReviewerPayload(row)
          : buildUpdatePayload();
      console.log("Payload for updateWordDetails:", payload);
      const res = await dispatch(validateSentenceDict(payload));
      if (res.status === 'success') {
        toast.success("Sentence validated!", {
          position: "top-center",
          autoClose: 2500,
        });
        getsentencesForUser(); // Refresh data
      } else {
        toast.error(res.message || "Validation failed", {
          position: "top-center",
        });
      }
      setEditingRowId(null)
    } catch (err) {
      console.error("Error validating sentence:", err);
      toast.error(`Error: ${err.message || "Validation failed"}`, {
        position: "top-center",
      });
    } finally {
      setLoading(false);
      activeValidationToast.current = null;
    }
  };

  const addField = (section) => {
    setSectionValues(prev => ({
      ...prev,
      [section]: [
        ...prev[section],
        section === "meanings"
          ? {
            uiId: crypto.randomUUID(),
            backendId: 0,
            value: "",
            partOfSpeech: ""
          }
          : {
            uiId: crypto.randomUUID(),
            backendId: 0,
            value: ""
          }
      ]
    }));
  };


  const removeField = (section, uiId) => {
    setSectionValues(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.uiId !== uiId),
    }));
  };


  const updateField = (section, uiId, updates) => {
    setSectionValues(prev => ({
      ...prev,
      [section]: prev[section].map(item =>
        item.uiId === uiId
          ? typeof updates === "object"
            ? { ...item, ...updates }
            : { ...item, value: updates }
          : item
      ),
    }));
  };


  const rejectSentence = async (row) => {

    setPendingAction("reject");

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md font-medium">
            Do you want to reject this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleReject(row);
              }}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm shadow-sm"
            >
              Yes, Reject
            </button>
            <button
              onClick={() => {
                closeToast();
                setPendingAction(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false, position: "top-center", hideProgressBar: true }
    );

    const handleReject = async (row) => {
      setLoading(true);
      try {
        const res = await dispatch(rejectSentenceForUserDict(row.original.mappingId, row.original.targetId, row.original.lotId));
        if (res.success) {
          toast.success("Sentence rejected successfully!");
          await getsentencesForUser();
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        toast.error("Failed to reject.");
      } finally {
        setLoading(false);
        setPendingAction(null);
      }
    };
  };

  const markAsResolved = async (row) => {
    setPendingAction("markAsResolved");

    // 🟠 Ask user for confirmation
    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md sm:text-base font-medium">
            Do you want to mark this sentence as resolved?
          </p>

          <div className="flex gap-3 justify-center">
            {/* YES BUTTON */}
            <button
              onClick={async () => {
                closeToast();
                await handleMarkAsResolved();
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              ✅ Yes, mark as resolved
            </button>

            {/* CANCEL BUTTON */}
            <button
              onClick={() => {
                toast.info("❌ Validation cancelled.", {
                  position: "top-center",
                  autoClose: 2000,
                });
                closeToast();
                setPendingAction(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
      }
    );

    // 🔹 Helper: Actual API call
    const handleMarkAsResolved = async () => {
      setLoading(true);
      setError(null);

      try {
        const absoluteIndex = pageIndex * pageSize + row.index;

        const payload = {
          mappingId: row.original.mappingId,
          targetId: row.original.targetId,
          updatedSourceText: "Corrected English source sentence after student review."
        };

        const res = await dispatch(resolveSentenceForUserDict(payload));

        if (res.success) {
          toast.success("🎉 Sentence marked as resolved successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          await getsentencesForUser();
          setDataVersion(v => v + 1); // ← Force remount
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        console.error("❌ Error resolving sentence:", err);
        toast.error("⚠️ Failed to mark as resolved. Try again.", {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        setPendingAction(null);
      }
    };
  };

  const validateSentence = async (row) => {
    const sentenceId = row.original.sentenceId;


    // Prevent multiple toasts for the same sentence
    if (activeValidationToast.current === sentenceId) {
      toast.info("Validation already in progress...", { toastId: "validation-in-progress" });
      return;
    }

    activeValidationToast.current = sentenceId; // Track current validation

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
            Do you want to validate this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleValidate();
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Yes, validate
            </button>
            <button
              onClick={() => {
                activeValidationToast.current = null; // Reset
                toast.dismiss("validation-toast"); // Dismiss this toast
                toast.info("Validation cancelled.", {
                  position: "top-center",
                  autoClose: 2000,
                });
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        toastId: "validation-toast", // ← THIS IS KEY: Only one toast with this ID
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
      }
    );

    const handleValidate = async () => {
      setLoading(true);
      try {

        console.log(row);
        const resWord = await dispatch(
          fetchWordDetails(row.original.mappingId, row.original.targetId)
        );
        let normalized = null;
        if (resWord?.success && resWord?.data) {
          normalized = role === 'REVIEWER' ? normalizeWordDetailsReviewer(resWord.data) : normalizeWordDetails(resWord.data);
        }
        console.log("Normalized word details:", normalized);
        const res = await dispatch(validateSentenceDict(normalized));
        if (res.status === 'success') {
          toast.success("Sentence validated!", {
            position: "top-center",
            autoClose: 2500,
          });
          getsentencesForUser(); // Refresh data
        } else {
          toast.error(res.message || "Validation failed", {
            position: "top-center",
          });
        }
      } catch (err) {
        console.error("Error validating sentence:", err);
        toast.error(`Error: ${err.message || "Validation failed"}`, {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        activeValidationToast.current = null; // Reset after done
      }
    };
  };

  const discardSentence = async (row) => {

    setPendingAction("discard");

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md font-medium">
            Do you want to discard this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleDiscard(row);
              }}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm shadow-sm"
            >
              Yes, Discard
            </button>
            <button
              onClick={() => {
                closeToast();
                setPendingAction(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false, position: "top-center", hideProgressBar: true }
    );

    const handleDiscard = async (row) => {
      setLoading(true);
      setError(null);

      try {
        const absoluteIndex = pageIndex * pageSize + row.index;

        const res = await dispatch(discardSentenceForUserDict(row.original.mappingId, row.original.targetId));

        if (res.success) {
          toast.success("🎉 Sentence discarded successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          await getsentencesForUser();
          setDataVersion(v => v + 1); // ← Force remount
          setDoubleClickedRow(null);
        } else {
          toast.error(`⚠️ Failed: ` + res.message, {
            position: "top-center",
          });
        }
      } catch (err) {
        console.error("❌ Error discard sentence:", err);
        toast.error("⚠️ Failed to discard. Try again.", {
          position: "top-center",
        });
        setDoubleClickedRow(null);
      } finally {
        setLoading(false);
      }
    };
  }

  const fileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Word (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.wordColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.wordColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer hover:underline` : row.original.wordColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.word}
          </span>
        ),
        meta: { width: "20%" },
      },
      {
        header: "Subdomain",
        cell: ({ row }) => (
          <span
            className={`font-medium block whitespace-pre-wrap`}>
            {row.original.subdomainName}
          </span>
        ),
        meta: { width: "20%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED" || row.original.studentStatus === "RESOLVED"
                ? "text-green-600"
                : row.original.studentStatus === "REJECTED"
                  ? "text-red-600"
                  : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "20%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED" || row.original.reviewerStatus === "RESOLVED"
                  ? "text-green-600"
                  : row.original.reviewerStatus === "REJECTED"
                    ? "text-red-600"
                    : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original.mappingId;
          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.wordColor === 'blue' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col items-center justify-center gap-2">
                {row.original.studentStatus === 'PENDING' && (
                  isEditing ? (
                    <div className="text-blue-600 font-medium">Editing ...</div>
                  ) : (
                    <button
                      onClick={() => startEdit(row)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 w-[150px]"
                    >
                      Edit In
                    </button>
                  )
                )}
                {(row.original.studentStatus === 'PENDING') && <button
                  onClick={() => reviewSentence(row)} className="px-4 py-1.5 w-[150px] rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                >Add Review</button>}
              </div>

          )
        },
        meta: { width: "12%" },
      }

    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role, editingRowId, dataVersion]);

  const reviewerFileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Word (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.wordColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.wordColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer hover:underline` : row.original.wordColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.word}
          </span>
        ),
        meta: { width: "20%" },
      },
      {
        header: "Subdomain",
        cell: ({ row }) => (
          <span
            className={`font-medium block whitespace-pre-wrap`}>
            {row.original.subdomainName}
          </span>
        ),
        meta: { width: "20%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED" || row.original.studentStatus === "RESOLVED"
                ? "text-green-600"
                : row.original.studentStatus === "REJECTED"
                  ? "text-red-600"
                  : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "20%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED" || row.original.reviewerStatus === "RESOLVED"
                  ? "text-green-600"
                  : row.original.reviewerStatus === "REJECTED"
                    ? "text-red-600"
                    : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original.mappingId;
          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.wordColor === 'blue' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col items-center justify-center gap-2">
                {row.original.studentStatus === 'PENDING' && (
                  isEditing ? (
                    <div className="text-blue-600 font-medium">Editing ...</div>
                  ) : (
                    <button
                      onClick={() => startEdit(row)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 w-[150px]"
                    >
                      Edit In
                    </button>
                  )
                )}
                {(row.original.studentStatus === 'PENDING') && <button
                  onClick={() => reviewSentence(row)} className="px-4 py-1.5 w-[150px] rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                >Add Review</button>}
              </div>

          )
        },
        meta: { width: "12%" },
      }

    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role, editingRowId, dataVersion]);

  const rejectedColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Source Text (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.sentenceColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.sourceText}
          </span>
        ),
        meta: { width: "35%" },
      },
      {
        header: "Target Audio (Record Here)",
        cell: ({ row }) => {
          const index = row.index;
          const isRowRecording = isRecording && recordingIndex === index;
          const isUploaded = tableData[index]?.isUploaded;
          const audioBase64 = row.original.audioUrl;

          // Function to play the base64 WAV audio
          const playRecording = async (studentId, recordingId) => {
            if (!studentId || !recordingId) {
              toast.error("Missing recording info");
              return;
            }

            setPlayModal({
              isOpen: true,
              audioUrl: null,
              loading: true,
              error: null
            });

            try {
              const url = role !== 'REVIEWER' ? `${BASE_URL}api/student/ts/recording?studentId=${studentId}&recordingId=${recordingId}` : `${BASE_URL}api/reviewer/ts/recording?reviewerId=${userId}&recordingId=${recordingId}`;

              // Add authentication if needed
              const response = await fetch(url, {
                credentials: "include",
                headers: {
                  "Authorization": `Bearer ${token}`, // if you have token
                }
              });

              if (!response.ok) throw new Error("Failed to load audio");

              const blob = await response.blob();
              const audioUrl = URL.createObjectURL(blob);

              setPlayModal(prev => ({
                ...prev,
                audioUrl,
                loading: false
              }));
            } catch (err) {
              console.error("Play audio error:", err);
              setPlayModal(prev => ({
                ...prev,
                loading: false,
                error: "Could not play recording. Please try again."
              }));
            }
          };

          return (
            <div className="flex flex-col items-start gap-2 items-center">

              {/* Play Button - Only show if audio exists */}
              {audioBase64 && (
                <button
                  onClick={() => playRecording(userId, row.original.recordingId)}
                  className={`w-[140px] h-[40px] flex items-center justify-center gap-2 rounded-md text-white font-medium shadow-md transition-all bg-blue-600 hover:bg-blue-700 active:scale-95`}
                >
                  <PlayCircle className="w-5 h-5" />
                  Play Recording
                </button>
              )}
            </div>
          );
        },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED" || row.original.studentStatus === "RESOLVED"
                ? "text-green-600"
                : row.original.studentStatus === "REJECTED"
                  ? "text-red-600"
                  : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "15%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED" || row.original.reviewerStatus === "RESOLVED"
                  ? "text-green-600"
                  : row.original.reviewerStatus === "REJECTED"
                    ? "text-red-600"
                    : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      role !== "REVIEWER" ? {
        header: "Actions",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const isSelected = absoluteIndex === selectedRow;

          const sentenceId = row.original.sentenceId;
          const isValidated = row.original.studentStatus === 'VALIDATED';
          const isEdited = isRowEdited(sentenceId);           // ← uses ref
          const shouldBeActive = !isValidated || isEdited;

          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.sentenceColor === 'blue' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col gap-2">
                <button onClick={() => validateSentence(row)}
                  disabled={!shouldBeActive || !editSentences}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${!shouldBeActive || !editSentences ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                  {shouldBeActive ? 'Validate' : 'Validated'}
                </button>
              </div>
          );
        }
      } : null
    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role, editingRowId, dataVersion]);

  // ← tableData REMOVED from deps!

  return (
    <div className="bg-[#fffaf7] min-h-screen">
      <Header />

      <div className="flex p-4">

        {/* Main Content */}
        <main
          className={`flex-1 pl-4 pr-4 bg-[#fffaf7] transition-all duration-300`}
        >
          <div className="bg-white shadow-lg rounded-2xl p-4 m-2 w-full">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-800">
                  Dictionary Dashboard
                </h2>
              </div>
            </div>

            {showMarkedSentencesDict && (
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setActiveTab("active")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "active"
                    ? "bg-amber-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences
                </button>

                <button
                  onClick={() => setActiveTab("rejected")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "rejected"
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences marked as issue
                </button>
              </div>
            )}


            {/* Data Table */}
            {files && files.length > 0 ? (
              <div className="border border-gray-200 rounded-xl">
                <GenericTableStudent
                  role={role}
                  data={paginatedData}
                  columns={role === 'REVIEWER' ? activeTab === 'active' ? reviewerFileColumns : rejectedColumns : activeTab === 'active' ? fileColumns : rejectedColumns}
                  showPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  getRowProps={(row) => {
                    const absoluteIndex = pageIndex * pageSize + row.index;
                    const isSelected = absoluteIndex === selectedRow;
                    return {
                      onClick: () => setSelectedRow(absoluteIndex),
                      style: {
                        backgroundColor: isSelected ? "#FFF6E5" : "white", // Highlight row
                        transition: "background 0.2s ease-in-out"
                      }
                    };
                  }}

                  hasNextPage={(pageIndex + 1) * pageSize < files.length}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                  editingRowId={editingRowId}
                  onEditRow={(row) => setEditingRowId(row.original.mappingId)}
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 py-10 text-lg">
                No {activeTab === 'active' ? 'sentences' : 'flagged sentences'} data available for this lot.
              </div>
            )}
          </div>
        </main>
      </div>
      <CommentsModal
        isOpen={commentsModal.isOpen}
        comments={commentsModal.comments}
        loading={commentsModal.loading}
        onClose={() => setCommentsModal({ isOpen: false, sentenceId: null, comments: [], loading: false })}
      />
      <ReviewModal
        isOpen={reviewModal.isOpen}
        row={reviewModal.row}
        comment={reviewModal.comment}
        translatedText={
          reviewModal.row
            ? tableDataRef.current[pageIndex * pageSize + reviewModal.row.index]?.targetText || ""
            : ""
        }
        onCommentChange={handleCommentChange}
        onClose={closeReviewModal}
        onSubmit={handleReviewSubmit}
        loading={loading}
      />
      {editModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl p-6 relative">

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-700">
                Edit Word Details (Language: {selectedTargetName} and domain: {domain})
              </h2>
              <button
                onClick={() => {
                  setEditModal({ isOpen: false, row: null });
                  setEditingRowId(null);
                }}
                className="text-gray-500 hover:text-red-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* Editor */}
            <EditWordEditor
              row={editModal.row?.original}
              loadingEdit={loadingEdit}
              sectionValues={sectionValues}
              setSectionValues={setSectionValues}
              addField={addField}
              updateField={updateField}
              removeField={removeField}
              partsOfSpeech={partsOfSpeech}
              onSave={() => {
                saveEdit(editModal.row);
                setEditModal({ isOpen: false, row: null });
              }}
              onCancel={() => {
                setEditModal({ isOpen: false, row: null });
                setEditingRowId(null);
              }}
              role={role}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default Dictionary
